import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ArrowLeft, HelpCircle } from 'lucide-react';

export default function TestManagement() {
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [seriesTitle, setSeriesTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', duration_minutes: 60, total_marks: 100, negative_marking: 0.25, subjects: '' });

  useEffect(() => { loadData(); }, [seriesId]);

  async function loadData() {
    try {
      const [seriesRes, testsRes] = await Promise.all([
        api.get(`/admin/test-series/${seriesId}`),
        api.get(`/admin/test-series/${seriesId}/tests`)
      ]);
      setSeriesTitle(seriesRes.data.title);
      setTests(testsRes.data);
    } catch {} finally { setLoading(false); }
  }

  function openCreate() { setEditing(null); setForm({ title: '', duration_minutes: 60, total_marks: 100, negative_marking: 0.25, subjects: '' }); setDialogOpen(true); }
  function openEdit(t) {
    setEditing(t);
    setForm({ title: t.title, duration_minutes: t.duration_minutes, total_marks: t.total_marks, negative_marking: t.negative_marking, subjects: (t.subjects || []).join(', ') });
    setDialogOpen(true);
  }

  async function handleSave() {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    const payload = { ...form, subjects: form.subjects ? form.subjects.split(',').map(s => s.trim()).filter(Boolean) : [] };
    try {
      if (editing) {
        await api.put(`/admin/tests/${editing.id}`, payload);
        toast.success('Updated');
      } else {
        await api.post(`/admin/test-series/${seriesId}/tests`, payload);
        toast.success('Created');
      }
      setDialogOpen(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this test and all its questions?')) return;
    try { await api.delete(`/admin/tests/${id}`); toast.success('Deleted'); loadData(); } catch { toast.error('Failed'); }
  }

  return (
    <div data-testid="test-management">
      <Button variant="ghost" className="mb-4" onClick={() => navigate('/admin/test-series')} data-testid="back-to-series">
        <ArrowLeft size={16} className="mr-2" /> Back to Test Series
      </Button>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-semibold text-[#0A0A0A]">Tests</h1>
          <p className="text-sm text-[#52525B]">Series: {seriesTitle}</p>
        </div>
        <Button className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white" onClick={openCreate} data-testid="create-test-btn">
          <Plus size={16} className="mr-2" /> Add Test
        </Button>
      </div>

      <Card className="border border-zinc-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Marks</TableHead>
              <TableHead>-ve Marking</TableHead>
              <TableHead>Questions</TableHead>
              <TableHead className="w-[120px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8">Loading...</TableCell></TableRow>
            ) : tests.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#52525B]">No tests yet</TableCell></TableRow>
            ) : tests.map((t, i) => (
              <TableRow key={t.id} data-testid={`test-row-${t.id}`}>
                <TableCell>{i + 1}</TableCell>
                <TableCell className="font-medium">{t.title}</TableCell>
                <TableCell>{t.duration_minutes} min</TableCell>
                <TableCell>{t.total_marks}</TableCell>
                <TableCell>{t.negative_marking}</TableCell>
                <TableCell><Badge variant="outline">{t.question_count || 0}</Badge></TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/tests/${t.id}/questions`)} data-testid={`manage-questions-${t.id}`}>
                      <HelpCircle size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`edit-test-${t.id}`}><Pencil size={14} /></Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(t.id)} data-testid={`delete-test-${t.id}`}><Trash2 size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-['Outfit']">{editing ? 'Edit Test' : 'Add Test'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="test-title-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Duration (min)</Label><Input type="number" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: Number(e.target.value) })} /></div>
              <div><Label>Total Marks</Label><Input type="number" value={form.total_marks} onChange={e => setForm({ ...form, total_marks: Number(e.target.value) })} /></div>
            </div>
            <div><Label>Negative Marking (per wrong)</Label><Input type="number" step="0.01" value={form.negative_marking} onChange={e => setForm({ ...form, negative_marking: Number(e.target.value) })} /></div>
            <div><Label>Subjects (comma separated)</Label><Input value={form.subjects} onChange={e => setForm({ ...form, subjects: e.target.value })} placeholder="Physics, Chemistry, Maths" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white" onClick={handleSave} data-testid="save-test-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
